import React, { useMemo } from "react";
import {
    FILE_TYPE_GROUPS,
    FileTypeGroup,
    EXTENSION_TO_FILE_TYPE_GROUP,
} from "@common/constants/financial-documents";
import { noop } from "@common/helpers/function";
import {
    FilterMultiselect,
    FilterMultiselectOption,
} from "@components/pageHeader/filters/FilterMultiselect/FilterMultiselect";
import { CsvIcon, DocIcon, JpgIcon, PdfIcon, PngIcon, XlsIcon } from "@icons";
import { HStack } from "@design-system/HStack/HStack";
import { BaseFilterProps } from "../../lib";
import styles from "../TransactionStatusFilter/TransactionStatusFilter.module.scss";

const FILE_TYPE_GROUP_ICONS: Record<
    FileTypeGroup,
    React.FC<React.SVGProps<SVGSVGElement>>
> = {
    [FileTypeGroup.PDF]: PdfIcon,
    [FileTypeGroup.CSV]: CsvIcon,
    [FileTypeGroup.EXCEL]: XlsIcon,
    [FileTypeGroup.WORD]: DocIcon,
    [FileTypeGroup.JPG]: JpgIcon,
    [FileTypeGroup.PNG]: PngIcon,
};

const options: FilterMultiselectOption[] = FILE_TYPE_GROUPS.map(({ label }) => {
    const Icon = FILE_TYPE_GROUP_ICONS[label];
    return {
        value: label,
        label: (
            <HStack as="span" align="center" gap={8}>
                <Icon width={16} height={16} className="icon-color-revert" />
                {label}
            </HStack>
        ),
        searchableLabel: label,
    };
});

export const DocumentTypeFilter: React.FC<BaseFilterProps> = ({
    filters,
    onChange,
}) => {
    // Derive selected FileTypeGroups from the flat extensions array
    const value = useMemo((): FileTypeGroup[] => {
        const exts = filters.fileExtensions ?? [];
        const groupSet = new Set<FileTypeGroup>();
        for (const ext of exts) {
            const group = EXTENSION_TO_FILE_TYPE_GROUP[ext];
            if (group) {
                groupSet.add(group);
            }
        }
        // Only include a group if ALL its extensions are selected
        return FILE_TYPE_GROUPS.filter(
            ({ label, extensions }) =>
                groupSet.has(label) &&
                extensions.every((e) => exts.includes(e)),
        ).map(({ label }) => label);
    }, [filters.fileExtensions]);

    return (
        <div className={styles.filter}>
            <FilterMultiselect
                inDropdown={false}
                searchable={false}
                options={options}
                value={value}
                onChange={(val) => {
                    const fileTypeGroups = Object.values(FileTypeGroup);
                    const selectedGroups = val.filter((v): v is FileTypeGroup =>
                        fileTypeGroups.includes(v as FileTypeGroup),
                    );
                    const extensions = FILE_TYPE_GROUPS.filter(({ label }) =>
                        selectedGroups.includes(label),
                    ).flatMap(({ extensions: exts }) => exts);
                    onChange({
                        fileExtensions:
                            extensions.length > 0 ? extensions : undefined,
                    });
                }}
                name="fileExtensions"
                onBlur={noop}
            />
        </div>
    );
};
